/**
 * AircraftDetailPanel — popover that opens when an operator clicks an
 * ADS-B aircraft on the live map.
 *
 * Mirrors TrackDetailPanel's pattern (slide-up from bottom-center,
 * dismissible). The map labels themselves are now just the callsign;
 * the rich detail (airline, type, registration, altitude, speed,
 * heading, vertical rate, squawk, ADS-B emitter category) lives here.
 *
 * What's NOT shown — origin / destination / scheduled times. That data
 * is not in the ADS-B feed; it requires a paid integration (FlightAware
 * AeroAPI, FR24 Business). When those are wired up the panel grows two
 * more rows; until then we honestly omit them rather than render "—".
 */
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMapStore } from '../../store/mapStore';

function formatAltitude(alt: number | null, onGround: boolean): string {
  if (onGround) return 'GROUND';
  if (alt == null) return '—';
  if (alt >= 18_000) {
    return `FL${Math.round(alt / 100).toString().padStart(3, '0')}`;
  }
  return `${alt.toLocaleString()} ft`;
}

function formatSpeed(kts: number | null): string {
  if (kts == null) return '—';
  return `${Math.round(kts).toLocaleString()} kts`;
}

function formatVerticalRate(fpm: number | null): string {
  if (fpm == null) return '—';
  if (Math.abs(fpm) < 100) return 'Level';
  const arrow = fpm > 0 ? '↑' : '↓';
  return `${arrow} ${Math.abs(Math.round(fpm)).toLocaleString()} ft/min`;
}

function formatHeading(deg: number | null): string {
  if (deg == null) return '—';
  const compass = ((Math.round(deg / 22.5) % 16) + 16) % 16;
  const cardinals = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return `${Math.round(deg)}° ${cardinals[compass]}`;
}

// ADS-B emitter category — A0-D7 codes describe what kind of aircraft is
// transmitting. Only the buckets a US-based operator routinely sees.
function categoryLabel(cat: string | null): string | null {
  if (!cat) return null;
  const map: Record<string, string> = {
    A0: 'No info',
    A1: 'Light (<15.5k lb)',
    A2: 'Small (15.5–75k lb)',
    A3: 'Large (75–300k lb)',
    A4: 'High vortex large',
    A5: 'Heavy (>300k lb)',
    A6: 'High performance',
    A7: 'Rotorcraft',
    B1: 'Glider / sailplane',
    B2: 'Lighter than air',
    B3: 'Parachutist',
    B4: 'Ultralight',
    B6: 'UAV',
    B7: 'Space vehicle',
    C1: 'Surface — emergency vehicle',
    C2: 'Surface — service vehicle',
    C3: 'Point obstacle',
  };
  return map[cat] ?? cat;
}

interface RowProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}

function Row({ label, value, mono = true }: RowProps) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-iron-700/60 last:border-b-0">
      <span className="text-[10px] font-mono font-semibold uppercase tracking-[0.18em] text-iron-400 flex-shrink-0">
        {label}
      </span>
      <span
        className={
          'text-sm text-iron-100 text-right truncate ' + (mono ? 'font-mono tabular-nums' : '')
        }
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

export default function AircraftDetailPanel() {
  const aircraft = useMapStore((s) => s.selectedAircraft);
  const close = useMapStore((s) => s.setSelectedAircraft);

  if (!aircraft) return null;

  const callsign = aircraft.flight?.trim() || aircraft.registration || aircraft.hex.toUpperCase();
  const altitude = formatAltitude(
    aircraft.altitudeBaro ?? aircraft.altitudeGeom,
    aircraft.onGround,
  );

  return (
    <div
      className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 w-[min(560px,calc(100vw-1.5rem))] glass-card shadow-xl"
      role="dialog"
      aria-label={`Aircraft ${callsign}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-iron-700">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-lg font-semibold text-iron-100 font-mono">{callsign}</span>
            {aircraft.airline && (
              <span className="text-sm text-accent">{aircraft.airline}</span>
            )}
          </div>
          <div className="text-[11px] text-iron-400 mt-0.5 font-mono">
            ICAO {aircraft.hex.toUpperCase()}
            {aircraft.registration && aircraft.registration !== callsign
              ? ` · ${aircraft.registration}`
              : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={() => close(null)}
          className="ml-3 p-1.5 text-iron-400 hover:text-iron-100 hover:bg-iron-700 rounded-sm flex-shrink-0"
          title="Close"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Body — two columns: flight state on left, identity on right.
          Stacks to one column on narrow viewports. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 px-4 py-3">
        <div>
          <div className="text-[10px] font-mono font-semibold uppercase tracking-[0.18em] text-iron-400 mb-1">
            Flight State
          </div>
          <Row label="Altitude" value={altitude} />
          <Row label="Ground Speed" value={formatSpeed(aircraft.groundSpeed)} />
          <Row label="Heading" value={formatHeading(aircraft.track)} />
          <Row label="Vert Rate" value={formatVerticalRate(aircraft.baroRate)} />
        </div>
        <div>
          <div className="text-[10px] font-mono font-semibold uppercase tracking-[0.18em] text-iron-400 mb-1 mt-3 sm:mt-0">
            Identity
          </div>
          <Row label="Type" value={aircraft.aircraftType ?? '—'} />
          <Row label="Category" value={categoryLabel(aircraft.category) ?? '—'} mono={false} />
          <Row label="Squawk" value={aircraft.squawk ?? '—'} />
          <Row
            label="Position"
            value={`${aircraft.latitude.toFixed(4)}°, ${aircraft.longitude.toFixed(4)}°`}
          />
        </div>
      </div>

      {/* Footer — honest disclosure about what we don't have. Subtle,
          single-line, mono. Sets the user's expectation; the moment we
          wire FlightAware this whole footer goes away. */}
      <div className="px-4 pb-3 text-[10px] font-mono text-iron-500 italic">
        Origin · destination · scheduled times not in ADS-B; would need a
        paid flight-data integration.
      </div>
    </div>
  );
}
